import { useState, type ReactNode } from 'react';
import type { Person } from '@domain/entities/Person';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { ReactivationResult, TerminationResult } from '@application/ports';
import { useQuery } from '@tanstack/react-query';
import {
  calculateSettlement,
  SEPARATION_KIND_LABEL,
  type SeparationKind,
} from '@domain/policies/SettlementPolicy';
import {
  Banner,
  Button,
  Field,
  Modal,
  inputClass,
  selectClass,
  textareaClass,
} from '@presentation/design-system';
import { useAccess } from '@presentation/hooks/useAccess';
import { useRuntimeConfig } from '@presentation/providers/RuntimeConfigProvider';
import { SettlementBreakdown, parsePesos } from './SettlementBreakdown';
import { reasonFor } from '@presentation/format';
import { getServices } from '@infrastructure/services';
import styles from './TerminationAction.module.css';

/**
 * Dar de baja y reactivar, en un solo botón junto al nombre.
 *
 * **Antes era una tarjeta propia en la rejilla**, y eso tenía dos problemas: le
 * dejaba un hueco a la pantalla cuando la persona estaba activa y no había nada
 * más que decir, y ponía la acción lejos del chip que dice el estado. En la
 * cabecera están juntos, y «Reactivar» sobre alguien cuyo chip dice «Dada de
 * baja» no necesita explicación.
 *
 * El detalle vive en un modal por lo mismo que el alta: son formularios con
 * consecuencias, no campos de una ficha, y no deben poder confirmarse a medio
 * scroll de otra cosa.
 *
 * ## Lo que estas dos operaciones arrastran
 *
 * - Una **baja** libera los liderazgos que la persona ejercía. Si nadie lo nota,
 *   su equipo queda con solicitudes esperando la firma de alguien que ya no está.
 * - Una **reactivación** decide si conserva su antigüedad, y eso cambia su prima
 *   de antigüedad, su aguinaldo proporcional y el periodo del que cuelga su saldo.
 *
 * Las dos devuelven qué movieron, y eso se enseña en vez de guardarse: no es un
 * acuse, es trabajo que acaba de aparecerle a quien administra el área.
 *
 * Lo que **no** puede prometer, y por eso no lo dice: cerrar el saldo del periodo
 * en curso, acuñar o revocar las claims de su sesión, y avisar. Las tres son de
 * servidor — ver `docs/KNPEOPLE.md` § People.
 */

export const TerminationAction = ({
  person,
  canEdit,
  pendingVacationDays,
  takenVacationDays,
  entitledDays,
  onDone,
}: {
  readonly person: Person;
  readonly canEdit: boolean;
  /**
   * Días generados y no gozados, que el finiquito paga.
   *
   * Llegan de la pantalla y no se recalculan aquí: el saldo real lo lleva el
   * módulo Time, con su periodo por aniversario y sus días prescritos. Un segundo
   * número aquí discreparía del que la persona ve en su portal.
   */
  readonly pendingVacationDays: number;
  /** Días del periodo que ya gozó. Del saldo, igual que los pendientes. */
  readonly takenVacationDays: number;
  /**
   * Días que le corresponden en el periodo — **el mismo número que enseña la
   * ficha**, no uno recalculado aquí.
   *
   * Se recalculaba con `TenurePolicy` y salía distinto: 26 en el modal contra 20
   * en el saldo de al lado, para la misma persona y en la misma pantalla. El
   * saldo puede llevar un derecho por contrato que la tabla de antigüedad no da,
   * así que recomputarlo ignora esa corrección — y dos cifras para lo mismo, una
   * junto a la otra, destruyen la confianza en las dos.
   */
  readonly entitledDays: number;
  readonly onDone: () => Promise<void> | void;
}): ReactNode => {
  const { holds } = useAccess();
  const config = useRuntimeConfig();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [kind, setKind] = useState<SeparationKind>('renuncia');
  const [assignSalary, setAssignSalary] = useState(false);
  /*
   * Apagada por defecto, al revés que la del alta.
   *
   * Crear un buzón de más se arregla borrándolo; borrar uno de menos destruye el
   * Drive de esa persona y a los 20 días Google ya no lo devuelve. Cuando las dos
   * equivocaciones no cuestan lo mismo, la casilla no arranca en la cara cara.
   */
  const [deleteGoogleAccount, setDeleteGoogleAccount] = useState(false);
  const [salaryDraft, setSalaryDraft] = useState('');
  /*
   * Los días de vacaciones, capturables.
   *
   * Los pendientes llegan del saldo del portal y **son editables**: ese saldo
   * cuenta lo que el portal sabe, y quien liquida suele tener días de antes de
   * que el portal existiera o acordados fuera. Prellenar y dejar corregir es
   * distinto de preguntar desde cero.
   */
  const [pendingDays, setPendingDays] = useState<string>('0');
  const [takenDays, setTakenDays] = useState<string>('0');
  const [primaPaid, setPrimaPaid] = useState(true);
  const [keepSeniority, setKeepSeniority] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [terminationDone, setTerminationDone] = useState<TerminationResult | null>(null);
  const [reactivationDone, setReactivationDone] = useState<ReactivationResult | null>(null);

  const canSeeSalary = holds('ver_salario');
  const canSetSalary = holds('editar_salario');

  const salary = useQuery({
    queryKey: ['salary', person.id],
    enabled: canSeeSalary && open,
    queryFn: async () => {
      const result = await getServices().salaries.getFor(person.id);
      return result.ok ? result.value : null;
    },
  });

  if (!canEdit) return null;

  const terminated = person.termination !== null;

  const storedCents = salary.data?.monthlyCents ?? null;
  const draftCents = parsePesos(salaryDraft);
  const salaryCents = storedCents ?? (assignSalary ? draftCents : null);

  const settlement =
    !canSeeSalary || salaryCents === null || person.hireDate === null || date.length === 0
      ? null
      : calculateSettlement({
          monthlySalaryCents: salaryCents,
          hireDate: person.hireDate,
          separationDate: CalendarDate.fromISOOrThrow(date),
          kind,
          pendingVacationDays: Number(pendingDays) || 0,
          takenVacationDays: Number(takenDays) || 0,
          primaPaidForTakenDays: primaPaid,
          annualVacationDays: entitledDays,
          unpaidWorkedDays: 0,
          params: {
            aguinaldoDays: config.rules.aguinaldoDays,
            primaVacacionalRate: config.rules.primaVacacionalRate,
            minimumWageCents: config.rules.minimumWageCents,
          },
        });

  const close = (): void => {
    setOpen(false);
    setFailure(null);
    setDate('');
    setReason('');
    setKeepSeniority(null);
    setTerminationDone(null);
    setReactivationDone(null);
  };

  const terminate = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    const outcome = await getServices().people.terminatePerson({
      personId: person.id,
      date,
      reason: reason.trim(),
      deleteGoogleAccount,
    });
    setBusy(false);
    if (!outcome.ok) {
      setFailure(`No se pudo dar de baja.${reasonFor(outcome.error)}`);
      return;
    }
    /*
     * El sueldo se guarda **después** de que la baja pasó, y sólo si alguien lo
     * pidió. Al revés dejaría a una persona con sueldo registrado y sin baja si lo
     * segundo fallara — un dato sensible escrito para una operación que no ocurrió.
     */
    if (assignSalary && draftCents !== null && canSetSalary) {
      await getServices().salaries.set({
        personId: person.id,
        monthlyCents: draftCents,
        effectiveFrom: person.hireDate?.toISO() ?? date,
      });
      await salary.refetch();
    }

    setTerminationDone(outcome.value);
    await onDone();
  };

  const reactivate = async (): Promise<void> => {
    if (keepSeniority === null) return;
    setBusy(true);
    setFailure(null);
    const outcome = await getServices().people.reactivatePerson({
      personId: person.id,
      keepSeniority,
      rehireDate: date,
    });
    setBusy(false);
    if (!outcome.ok) {
      setFailure(`No se pudo reactivar.${reasonFor(outcome.error)}`);
      return;
    }
    setReactivationDone(outcome.value);
    await onDone();
  };

  const done = terminationDone !== null || reactivationDone !== null;

  return (
    <>
      <Button
        variant={terminated ? 'ghost' : 'danger'}
        onClick={() => {
          /*
           * Los días se copian **al abrir**, no al montar.
           *
           * Estaban en el valor inicial de `useState`, que React lee una sola vez —
           * y esta tarjeta se monta con la ficha, cuando la consulta del saldo
           * todavía no ha vuelto. El modal abría con «0 tomados, 0 pendientes»
           * para alguien que en la ficha de al lado mostraba 5 y 15, y el finiquito
           * salía sin un peso de vacaciones.
           *
           * Es el mismo patrón que ya mordió en el panel administrador: un valor
           * inicial de `useState` no sigue a la prop que lo alimentó.
           */
          setPendingDays(String(pendingVacationDays));
          setTakenDays(String(takenVacationDays));
          setOpen(true);
        }}
      >
        {terminated ? 'Reactivar' : 'Dar de baja'}
      </Button>

      {open && (
        <Modal
          title={terminated ? `Reactivar a ${person.displayName}` : `Dar de baja a ${person.displayName}`}
          size={settlement !== null && !done ? 'wide' : 'default'}
          onClose={close}
          footer={
            done ? (
              <Button variant="primary" onClick={close}>
                Cerrar
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={close}>
                  Cancelar
                </Button>
                <Button
                  variant={terminated ? 'primary' : 'danger'}
                  disabled={
                    busy ||
                    date.length === 0 ||
                    (terminated ? keepSeniority === null : reason.trim().length === 0)
                  }
                  onClick={() => {
                    void (terminated ? reactivate() : terminate());
                  }}
                >
                  {busy
                    ? 'Guardando…'
                    : terminated
                      ? 'Confirmar reactivación'
                      : 'Confirmar baja'}
                </Button>
              </>
            )
          }
        >
          <div className={styles.body}>
            {terminationDone !== null ? (
              <>
              <Banner
                tone={
                  terminationDone.leadershipsReleased === 0 &&
                  terminationDone.subAreasWithoutLead.length === 0
                    ? 'success'
                    : 'warn'
                }
              >
                {terminationDone.leadershipsReleased === 0 &&
                terminationDone.subAreasWithoutLead.length === 0
                  ? 'Baja registrada. No lideraba a nadie, así que no dejó firmas pendientes.'
                  : `Baja registrada. Dejó de liderar a ${String(terminationDone.reportsAffected)} ${
                      terminationDone.reportsAffected === 1 ? 'persona' : 'personas'
                    }${
                      terminationDone.subAreasWithoutLead.length > 0
                        ? `, y estas sub áreas se quedaron sin responsable: ${terminationDone.subAreasWithoutLead.join(', ')}.`
                        : '.'
                    } Asígnales quién firma antes de que pidan días.`}
              </Banner>

              {/*
                La cuenta se reporta aparte de la baja, y no se mezcla en su banner.
                Son dos hechos independientes: la baja siempre queda registrada, y el
                buzón puede haberse borrado, no haber existido o haberse resistido.
                Un solo mensaje que los juntara obligaría a leerlo entero para saber
                si hay algo que hacer.
              */}
              {terminationDone.googleAccount === 'deleted' ? (
                <Banner tone="success">
                  Su cuenta de Google quedó borrada. Google la conserva 20 días por si
                  hubo un error; después ya no se recupera.
                </Banner>
              ) : terminationDone.googleAccount === 'failed' ? (
                <Banner tone="danger">{terminationDone.googleAccountNote}</Banner>
              ) : deleteGoogleAccount ? (
                /*
                  Se pidió borrarla y no se borró. Callar aquí sería lo peor de las
                  tres opciones: quien dio la baja se queda creyendo que la cuenta ya
                  no existe. El motivo casi siempre es que nunca tuvo buzón —son 141
                  personas del Excel— y eso no es un fallo, pero hay que decirlo.
                */
                <Banner tone="info">{terminationDone.googleAccountNote}</Banner>
              ) : null}
              </>
            ) : reactivationDone !== null ? (
              <>
                <Banner tone={reactivationDone.keptSeniority ? 'success' : 'warn'}>
                  {reactivationDone.keptSeniority
                    ? `Vuelve al padrón conservando su antigüedad: ${String(reactivationDone.years)} años desde ${reactivationDone.hireDate}.`
                    : `Vuelve como relación laboral nueva desde ${reactivationDone.hireDate}. Pierde ${String(reactivationDone.previousYears)} años de antigüedad, y con ellos los días de vacaciones que esa antigüedad le daba.`}
                </Banner>
                <p className={styles.note}>
                  La baja anterior queda en la bitácora de movimientos. En producción su
                  acceso se vuelve a acuñar en su siguiente inicio de sesión.
                </p>
              </>
            ) : terminated ? (
              <ReactivateForm
                person={person}
                date={date}
                onDate={(value) => {
                  setDate(value);
                  setFailure(null);
                }}
                keepSeniority={keepSeniority}
                onKeep={setKeepSeniority}
              />
            ) : (
              <div className={settlement !== null ? styles.twoColumns : undefined}>
                <div className={styles.column}>
                  <p className={styles.note}>
                    Libera los liderazgos que ejerza, así que su equipo se queda sin quien
                    le firme hasta que le asignes otro.
                  </p>

                  <Field
                    label="Último día"
                    hint="El día en que deja de contar, no el día en que se captura."
                  >
                    {(id) => (
                      <input
                        id={id}
                        type="date"
                        className={inputClass()}
                        value={date}
                        onChange={(event) => {
                          setDate(event.target.value);
                          setFailure(null);
                        }}
                      />
                    )}
                  </Field>

                  {/*
                    * El tipo va aparte del motivo, y no es duplicación.
                    *
                    * El motivo es la explicación humana y va a la bitácora. El tipo
                    * **decide qué se le debe**: un despido injustificado genera tres
                    * meses y prima de antigüedad, una renuncia no. Derivarlo del texto
                    * libre sería adivinar sobre dinero.
                    */}
                  <Field
                    label="Tipo de separación"
                    hint="Determina qué se le debe. No es una etiqueta."
                  >
                    {(id) => (
                      <select
                        id={id}
                        className={selectClass()}
                        value={kind}
                        onChange={(event) => {
                          setKind(event.target.value as SeparationKind);
                          if (reason.trim().length === 0) {
                            setReason(SEPARATION_KIND_LABEL[event.target.value as SeparationKind]);
                          }
                        }}
                      >
                        {(Object.keys(SEPARATION_KIND_LABEL) as SeparationKind[]).map((k) => (
                          <option key={k} value={k}>
                            {SEPARATION_KIND_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>

                  <Field label="Motivo" hint="Lo que quedará en la bitácora.">
                    {(id) => (
                      <textarea
                        id={id}
                        className={textareaClass()}
                        rows={2}
                        value={reason}
                        placeholder="Por qué deja la empresa"
                        onChange={(event) => {
                          setReason(event.target.value);
                          setFailure(null);
                        }}
                      />
                    )}
                  </Field>

                  {/*
                    * El sueldo, sólo para quien puede verlo. Sin `ver_salario` no se
                    * insinúa que exista: un recuadro que diga «no tienes acceso»
                    * confirma que hay un sueldo y cuánto pesa en la pantalla.
                    */}
                  {/*
                    * Los días que gobiernan la prima vacacional.
                    *
                    * Se piden los tres —le corresponden, tomó, le quedan— porque el
                    * artículo 80 manda la prima sobre los que **corresponden** y no
                    * sobre los que sobraron. Sin el dato de los gozados, quien tomó
                    * sus vacaciones y nunca cobró la prima la pierde en su finiquito,
                    * y el número resultante se ve razonable.
                    */}
                  {canSeeSalary && (
                    <fieldset className={styles.choice}>
                      <legend className={styles.choiceLegend}>Vacaciones del periodo</legend>

                      <p className={styles.note}>
                        Le corresponden <strong>{entitledDays} días</strong> por su
                        antigüedad.
                      </p>

                      <div className={styles.dayGrid}>
                        <Field label="Ya tomados">
                          {(id) => (
                            <input
                              id={id}
                              type="number"
                              min={0}
                              className={inputClass()}
                              value={takenDays}
                              onChange={(event) => {
                                setTakenDays(event.target.value);
                              }}
                            />
                          )}
                        </Field>

                        <Field label="Pendientes" hint="Del saldo del portal. Se puede corregir.">
                          {(id) => (
                            <input
                              id={id}
                              type="number"
                              min={0}
                              className={inputClass()}
                              value={pendingDays}
                              onChange={(event) => {
                                setPendingDays(event.target.value);
                              }}
                            />
                          )}
                        </Field>
                      </div>

                      <label className={styles.option}>
                        <input
                          type="checkbox"
                          checked={primaPaid}
                          onChange={(event) => {
                            setPrimaPaid(event.target.checked);
                          }}
                        />
                        <span>
                          <span className={styles.optionLabel}>
                            Ya se le pagó la prima de los días tomados
                          </span>
                          <span className={styles.optionNote}>
                            Si no, se le debe el {Math.round(config.rules.primaVacacionalRate * 100)}%
                            también sobre esos días. El portal no lleva ese registro, lo
                            lleva nómina.
                          </span>
                        </span>
                      </label>
                    </fieldset>
                  )}

                  {/*
                    * Quien da de baja sin `ver_salario` no se queda en silencio.
                    *
                    * La regla de no insinuar el sueldo sigue en pie: el aviso no dice
                    * si esta persona tiene uno ni cuánto. Dice **quién calcula el
                    * finiquito**, que es proceso y no dato — y callarlo dejaba a quien
                    * acaba de dar una baja sin saber que falta un paso.
                    */}
                  {!canSeeSalary && (
                    <Banner tone="info">
                      El finiquito lo calcula Finanzas desde la ficha de la persona.
                    </Banner>
                  )}

                  {/*
                    Irreversible, y por eso se pide aparte y arranca apagada.
                    La baja por sí sola ya le corta la sesión y sus permisos: esto
                    es lo otro, quitarle el correo y con él su Drive.
                  */}
                  <label className={styles.option}>
                    <input
                      type="checkbox"
                      checked={deleteGoogleAccount}
                      onChange={(event) => {
                        setDeleteGoogleAccount(event.target.checked);
                      }}
                    />
                    <span>
                      <span className={styles.optionLabel}>
                        Borrar también su cuenta de Google
                      </span>
                      <span className={styles.optionNote}>
                        Libera su licencia de Workspace y destruye su correo y su Drive.
                        Google los conserva 20 días y después no hay vuelta: rescata
                        antes lo que haga falta. Sin esto, la baja igual le corta la
                        sesión y sus permisos en el portal.
                      </span>
                    </span>
                  </label>

                  {canSeeSalary && storedCents === null && (
                    canSetSalary ? (
                      <>
                        <label className={styles.option}>
                          <input
                            type="checkbox"
                            checked={assignSalary}
                            onChange={(event) => {
                              setAssignSalary(event.target.checked);
                            }}
                          />
                          <span>
                            <span className={styles.optionLabel}>
                              Asignarle un sueldo ahora
                            </span>
                            <span className={styles.optionNote}>
                              No tiene ninguno registrado, y sin él no hay finiquito que
                              calcular. Se guarda junto con la baja.
                            </span>
                          </span>
                        </label>

                        {assignSalary && (
                          <Field label="Sueldo mensual">
                            {(id) => (
                              <input
                                id={id}
                                className={inputClass()}
                                inputMode="decimal"
                                placeholder="30,000.00"
                                value={salaryDraft}
                                onChange={(event) => {
                                  setSalaryDraft(event.target.value);
                                }}
                              />
                            )}
                          </Field>
                        )}
                      </>
                    ) : (
                      <Banner tone="info">
                        No tiene sueldo registrado, así que no hay finiquito que calcular.
                        Lo asigna Finanzas desde su ficha.
                      </Banner>
                    )
                  )}
                </div>

                {settlement !== null && (
                  <div className={styles.column}>
                    <SettlementBreakdown settlement={settlement} />
                  </div>
                )}
              </div>
            )}

            {failure !== null && <Banner tone="danger">{failure}</Banner>}
          </div>
        </Modal>
      )}
    </>
  );
};

const ReactivateForm = ({
  person,
  date,
  onDate,
  keepSeniority,
  onKeep,
}: {
  readonly person: Person;
  readonly date: string;
  readonly onDate: (value: string) => void;
  readonly keepSeniority: boolean | null;
  readonly onKeep: (value: boolean) => void;
}): ReactNode => (
  <>
    {person.termination !== null && (
      <p className={styles.note}>
        Se fue el {person.termination.date.toISO()} · {person.termination.reason}
      </p>
    )}

    <Field label="Vuelve desde" hint="Desde cuándo cuenta otra vez.">
      {(id) => (
        <input
          id={id}
          type="date"
          className={inputClass()}
          value={date}
          onChange={(event) => {
            onDate(event.target.value);
          }}
        />
      )}
    </Field>

    {/*
      * La pregunta que gobierna todo, y por eso no tiene valor por omisión.
      *
      * Preseleccionar «conservar» haría que reiniciar una antigüedad ocurriera
      * sólo a propósito, que suena bien — y preseleccionar lo contrario le
      * quitaría años a alguien por no leer. Sin omisión, el botón no se enciende
      * hasta que alguien decide.
      */}
    <fieldset className={styles.choice}>
      <legend className={styles.choiceLegend}>¿Conserva su antigüedad?</legend>

      <label className={styles.option}>
        <input
          type="radio"
          name="seniority"
          checked={keepSeniority === true}
          onChange={() => {
            onKeep(true);
          }}
        />
        <span>
          <span className={styles.optionLabel}>Sí, la conserva</span>
          <span className={styles.optionNote}>
            La baja fue un error o hubo reinstalación: la relación laboral nunca se
            interrumpió. Mantiene su fecha de ingreso
            {person.hireDate === null ? '' : ` (${person.hireDate.toISO()})`}, su derecho
            de vacaciones y su prima de antigüedad.
          </span>
        </span>
      </label>

      <label className={styles.option}>
        <input
          type="radio"
          name="seniority"
          checked={keepSeniority === false}
          onChange={() => {
            onKeep(false);
          }}
        />
        <span>
          <span className={styles.optionLabel}>No, empieza de cero</span>
          <span className={styles.optionNote}>
            Relación laboral nueva. Su fecha de ingreso pasa a ser la de regreso y vuelve
            al mínimo de ley: pierde los años acumulados y los días que le daban.
          </span>
        </span>
      </label>
    </fieldset>
  </>
);
